import { Button, Stack, Typography } from '@mui/material'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { GoogleLoginButton } from 'components/Login'
import { COLORS } from 'app-constants'
import { ReactComponent as GmailIcon } from 'components/Permissions/icons/gmail-icon.svg'
import { ReactComponent as GoogleDriveIcon } from 'components/Permissions/icons/google-drive-icon.svg'
import { ReactComponent as CalendarIcon } from 'components/Permissions/icons/google-calendar-icon.svg'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import { Feature } from 'api/model'

export const PermissionsButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onClick}
      size="small"
      sx={{
        width: 'fit-content',
        color: COLORS.PURE_WHITE,
      }}>
      Grant permissions
    </Button>
  )
}

const SetupPermissionsModal = ({
  acceptedPermissions,
  additionalParams,
  missingPermissions,
  isFromChatbot,
  onClose,
  feature,
}: {
  acceptedPermissions: string[]
  additionalParams?: Record<string, string>
  missingPermissions: string[]
  isFromChatbot?: boolean
  onClose: () => void
  feature: Feature | null
}) => {
  const isMobile = useCheckMobileScreen()

  const containsGmailPermission = missingPermissions.find((p) => p.includes('gmail'))
  const containsUserInfoPermission = missingPermissions.find((p) => p.includes('userinfo'))
  const containsCalendarPermission = missingPermissions.find((p) => p.includes('calendar'))
  const containsDrivePermission = missingPermissions.find((p) => p.includes('drive'))

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
        maxWidth: '642px',
        maxHeight: isMobile ? '75vh' : '80vh',
      }}>
      <Typography
        variant="h5"
        mb={2}
        sx={{
          color: COLORS.GRAY_0,
        }}>
        Why we need access
      </Typography>

      <Stack mt={2} spacing={2}>
        {containsGmailPermission && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <GmailIcon
              style={{
                flex: '0 0 24px',
              }}
            />
            <Typography variant="pS">
              {isFromChatbot
                ? 'To gain context-awareness from your email content and provide you detailed insights.'
                : 'To apply labels and relocate emails to other labels.'}
            </Typography>
          </Stack>
        )}
        {containsCalendarPermission && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <CalendarIcon
              style={{
                flex: '0 0 24px',
              }}
            />
            <Typography variant="pS">
              {isFromChatbot
                ? 'To gain context-awareness from your calendar events and provide you insights regarding meetings and scheduling'
                : 'To enhance the context-awareness of insights and generated drafts that involve meetings and scheduling.'}
            </Typography>
          </Stack>
        )}

        {containsDrivePermission && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <GoogleDriveIcon
              style={{
                flex: '0 0 24px',
              }}
            />
            <Typography variant="pS">
              To use the context in files to enhance your generated drafts. You can control our access to individual
              files.
            </Typography>
          </Stack>
        )}

        {containsUserInfoPermission && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <AccountCircleOutlinedIcon
              sx={{
                fill: COLORS.JELLED_PRIMARY_BLUE,
              }}
            />
            <Typography variant="pS">To access your email signature when automatically creating drafts.</Typography>
          </Stack>
        )}
      </Stack>

      <ModalButtonsContainerStack>
        <Button
          onClick={() => onClose()}
          size="medium"
          sx={{
            color: 'primary.main',
          }}>
          Cancel
        </Button>

        <GoogleLoginButton
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          additionalParams={additionalParams}
          feature={feature}
        />
      </ModalButtonsContainerStack>
    </JelledModal>
  )
}

export default SetupPermissionsModal
